// src/pages/system/Zones.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

const ZoneForm = ({ open, onClose, onSubmit, zone = null }) => {
  const formatTimeForInput = (timeStr) => {
    if (!timeStr) return '00:00';
    return timeStr.slice(0, 5);
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    billing_day: 1,
    payment_day: 15,
    cutoff_day: 20,
    billing_time: '00:00',
    payment_time: '00:00',
    cutoff_time: '00:00',
    ...zone
  });

  useEffect(() => {
    if (zone) {
      setFormData({
        name: zone.name || '',
        description: zone.description || '',
        billing_day: zone.billing_day || 1,
        payment_day: zone.payment_day || 15,
        cutoff_day: zone.cutoff_day || 20,
        billing_time: formatTimeForInput(zone.billing_time),
        payment_time: formatTimeForInput(zone.payment_time),
        cutoff_time: formatTimeForInput(zone.cutoff_time),
      });
    } else {
      setFormData({
        name: '',
        description: '',
        billing_day: 1,
        payment_day: 15,
        cutoff_day: 20,
        billing_time: '00:00',
        payment_time: '00:00',
        cutoff_time: '00:00',
      });
    }
  }, [zone]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('_day') ? parseInt(value) || '' : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {zone ? 'Editar Zona' : 'Nueva Zona'}
        </DialogTitle>
        <DialogContent>
          <Box className="space-y-4 py-4">
            <TextField
              fullWidth
              label="Nombre de la Zona"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              margin="normal"
            />
            <TextField
              fullWidth
              label="Descripción"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              margin="normal"
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Configuración de Ciclo de Facturación
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Día de facturación"
                name="billing_day"
                type="number"
                value={formData.billing_day}
                onChange={handleChange}
                inputProps={{ min: 1, max: 31 }}
                helperText="Día del mes"
              />
              <TextField
                fullWidth
                label="Hora de facturación"
                name="billing_time"
                type="time"
                value={formData.billing_time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                helperText="Hora del día"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Día de vencimiento de pago"
                name="payment_day"
                type="number"
                value={formData.payment_day}
                onChange={handleChange}
                inputProps={{ min: 1, max: 31 }}
                helperText="Día del mes"
              />
              <TextField
                fullWidth
                label="Hora de vencimiento"
                name="payment_time"
                type="time"
                value={formData.payment_time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                helperText="Hora del día"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Día de corte"
                name="cutoff_day"
                type="number"
                value={formData.cutoff_day}
                onChange={handleChange}
                inputProps={{ min: 1, max: 31 }}
                helperText="Día del mes"
              />
              <TextField
                fullWidth
                label="Hora de corte"
                name="cutoff_time"
                type="time"
                value={formData.cutoff_time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                helperText="Hora del día"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" color="primary">
            Guardar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Zones = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState(null);

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await fetch('/api/zones', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setZones(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching zones:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      const method = selectedZone ? 'PUT' : 'POST';
      const url = selectedZone
        ? `/api/zones/${selectedZone.id}`
        : '/api/zones';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Error al guardar la zona');

      fetchZones();
      setOpenForm(false);
      setSelectedZone(null);
    } catch (error) {
      console.error('Error saving zone:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/zones/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Error al eliminar la zona');

      fetchZones();
      setDeleteConfirmOpen(false);
      setZoneToDelete(null);
    } catch (error) {
      console.error('Error deleting zone:', error);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Nombre', width: 180 },
    { field: 'description', headerName: 'Descripción', width: 250 },
    {
      field: 'clientCount',
      headerName: 'Total Clientes',
      width: 130,
      type: 'number'
    },
    {
      field: 'billing_day',
      headerName: 'Ciclo de Facturación',
      width: 220,
      renderCell: (params) => {
        const day = params.row.billing_day ? `Día ${params.row.billing_day}` : 'No definido';
        const time = params.row.billing_time ? params.row.billing_time.slice(0, 5) : '00:00';
        return params.row.billing_day ? `${day} a las ${time}` : 'No definido';
      }
    },
    {
      field: 'payment_day',
      headerName: 'Vencimiento de Pago',
      width: 200,
      renderCell: (params) => {
        const day = params.row.payment_day ? `Día ${params.row.payment_day}` : 'No definido';
        const time = params.row.payment_time ? params.row.payment_time.slice(0, 5) : '00:00';
        return params.row.payment_day ? `${day} a las ${time}` : 'No definido';
      }
    },
    {
      field: 'cutoff_day',
      headerName: 'Corte de Servicio',
      width: 200,
      renderCell: (params) => {
        const day = params.row.cutoff_day ? `Día ${params.row.cutoff_day}` : 'No definido';
        const time = params.row.cutoff_time ? params.row.cutoff_time.slice(0, 5) : '00:00';
        return params.row.cutoff_day ? `${day} a las ${time}` : 'No definido';
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedZone(params.row);
                setOpenForm(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setZoneToDelete(params.row);
                setDeleteConfirmOpen(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 4 } }}>
      {/* Cabecera Premium */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Zonas
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
            Configure las áreas de cobertura y sus respectivos ciclos de facturación.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedZone(null);
            setOpenForm(true);
          }}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            py: 1.2,
            backgroundColor: '#6366f1',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
            '&:hover': { backgroundColor: '#4f46e5' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          Nueva Zona
        </Button>
      </Box>

      {/* Tabla Premium */}
      <Paper
        sx={{
          borderRadius: '24px',
          boxShadow: '0 4px 30px rgba(0,0,0,0.02)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden',
          backgroundColor: 'white',
          p: 1
        }}
      >
        <DataGrid
          rows={zones}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          sx={{
            border: 'none',
            fontFamily: 'Inter, sans-serif',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#475569',
                fontSize: '0.85rem'
              }
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(0,0,0,0.04)',
              color: '#334155',
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                transform: 'scale(1.002)',
              }
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.12)',
              }
            },
          }}
        />
      </Paper>

      <ZoneForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedZone(null);
        }}
        onSubmit={handleSubmit}
        zone={selectedZone}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas eliminar la zona "{zoneToDelete?.name}"?
          {zoneToDelete?.clientCount > 0 && (
            <Typography color="error" className="mt-2">
              ¡Advertencia! Esta zona tiene {zoneToDelete.clientCount} clientes asignados.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => handleDelete(zoneToDelete.id)}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Zones;